import { priceTicksToView, type ClobTradeRow } from '@cpc/tx';

import { getPushHubHttpUrl } from '~/config/clobConfig';
import { tryNormalizeToHexAddress } from '~/utils/cpcAddress';

export type PushHubTradeStats = {
	total_count?: number;
	total_volume_lots?: string;
	recent_5m_count?: number;
	recent_1h_count?: number;
	recent_24h_count?: number;
	recent_24h_volume_lots?: string;
	unique_traders_24h?: number;
	buy_count_24h?: number;
	sell_count_24h?: number;
	last_price_ticks?: string;
	last_qty_lots?: string;
	last_timestamp_ms?: number;
};

export type PushHubOrderStats = {
	open_orders?: number;
	total_orders?: number;
};

export type PushHubHealth = {
	ok?: boolean;
	depth_ch?: string;
	trade_ch?: string;
	orders_ch?: string;
	kline_ch_prefix?: string;
	pair?: string;
	chain_events?: number;
	hub_events?: number;
	latest_checkpoint?: number;
	checkpoint_cursor?: number;
	trade_stats?: PushHubTradeStats;
	order_stats?: PushHubOrderStats;
};

export type PushHubKlineCandle = {
	interval?: string;
	open_time_ms?: number;
	open_ticks?: string;
	high_ticks?: string;
	low_ticks?: string;
	close_ticks?: string;
	volume_lots?: string;
	closed?: boolean;
};

export type PushTradeWire = {
	tx_digest?: string;
	event_seq?: string;
	price_ticks?: string;
	qty_lots?: string;
	maker?: string;
	taker?: string;
	taker_side?: string;
	timestamp_ms?: number;
};

const DEFAULT_PAIR = 'DATA-CPC';

export function defaultPushHubChannels(
	klineInterval: string,
	pair = DEFAULT_PAIR,
): {
	depthCh: string;
	tradeCh: string;
	klineCh: string;
	ordersCh: string;
} {
	return {
		depthCh: `depth:${pair}`,
		tradeCh: `trade:${pair}`,
		klineCh: `kline:${pair}:${klineInterval}`,
		// Hub has one wallet orders channel (primary pair label), not per-market.
		ordersCh: `orders:${DEFAULT_PAIR}`,
	};
}

export async function fetchPushHubHealth(): Promise<PushHubHealth> {
	const res = await fetch(`${getPushHubHttpUrl()}/health`);
	if (!res.ok) throw new Error(`Push Hub health HTTP ${res.status}`);
	return (await res.json()) as PushHubHealth;
}

export async function fetchPushHubKline(
	interval = '1h',
	limit = 48,
): Promise<PushHubKlineCandle[]> {
	const params = new URLSearchParams({ interval, limit: String(limit) });
	const res = await fetch(`${getPushHubHttpUrl()}/v1/kline?${params}`);
	if (!res.ok) throw new Error(`Push Hub kline HTTP ${res.status}`);
	const body = (await res.json()) as { klines?: PushHubKlineCandle[] };
	return body.klines ?? [];
}

export type ClobTraderRank = {
	address: string;
	trade_count?: number;
	volume_lots?: number;
};

export type ClobOrderRank = {
	address: string;
	order_count?: number;
};

export type AddressStatRank = {
	address: string;
	cpc_mist?: string;
	data_mist?: string;
	stake_mist?: string;
	updated_ms?: number;
};

export type PushHubLeaderboardResponse<T> = {
	pair?: string;
	metric?: string;
	scope?: 'full_chain' | 'sampled' | string;
	window?: string;
	since_ms?: number;
	sample_size?: number;
	updated_ms?: number;
	disclaimer?: string;
	leaderboard?: T[];
};

export async function fetchClobTraderLeaderboard(
	window = '24h',
	limit = 20,
): Promise<ClobTraderRank[]> {
	const params = new URLSearchParams({ window, limit: String(limit) });
	const res = await fetch(`${getPushHubHttpUrl()}/v1/leaderboard/traders?${params}`);
	if (!res.ok) throw new Error(`Push Hub leaderboard HTTP ${res.status}`);
	const body = (await res.json()) as PushHubLeaderboardResponse<ClobTraderRank>;
	return body.leaderboard ?? [];
}

export async function fetchClobOrderLeaderboard(limit = 20): Promise<ClobOrderRank[]> {
	const params = new URLSearchParams({ limit: String(limit) });
	const res = await fetch(`${getPushHubHttpUrl()}/v1/leaderboard/orders?${params}`);
	if (!res.ok) throw new Error(`Push Hub order leaderboard HTTP ${res.status}`);
	const body = (await res.json()) as PushHubLeaderboardResponse<ClobOrderRank>;
	return body.leaderboard ?? [];
}

export async function fetchCpcBalanceLeaderboard(limit = 20): Promise<AddressStatRank[]> {
	const params = new URLSearchParams({ limit: String(limit) });
	const res = await fetch(`${getPushHubHttpUrl()}/v1/leaderboard/balances?${params}`);
	if (!res.ok) throw new Error(`Push Hub balance leaderboard HTTP ${res.status}`);
	const body = (await res.json()) as PushHubLeaderboardResponse<AddressStatRank>;
	return body.leaderboard ?? [];
}

export type CpcHolderRankResponse = {
	rows: AddressStatRank[];
	scope?: string;
	disclaimer?: string;
	sample_size?: number;
	updated_ms?: number;
};

/** Prefer full-chain holders from Indexer scan; fall back to sampled CLOB addresses. */
export async function fetchCpcHolderLeaderboard(limit = 20): Promise<CpcHolderRankResponse> {
	const params = new URLSearchParams({ limit: String(limit) });
	const res = await fetch(`${getPushHubHttpUrl()}/v1/leaderboard/holders?${params}`);
	if (res.ok) {
		const body = (await res.json()) as PushHubLeaderboardResponse<AddressStatRank>;
		const rows = body.leaderboard ?? [];
		if (rows.length > 0 || body.scope === 'full_chain') {
			return {
				rows,
				scope: body.scope,
				disclaimer: body.disclaimer,
				sample_size: body.sample_size,
				updated_ms: body.updated_ms,
			};
		}
	}
	const sampled = await fetchCpcBalanceLeaderboard(limit);
	return {
		rows: sampled,
		scope: 'sampled',
		disclaimer: 'Sampled from CLOB-known addresses via fullnode RPC',
	};
}

export async function fetchUserStakeLeaderboard(limit = 20): Promise<AddressStatRank[]> {
	const params = new URLSearchParams({ limit: String(limit) });
	const res = await fetch(`${getPushHubHttpUrl()}/v1/leaderboard/user_stakes?${params}`);
	if (!res.ok) throw new Error(`Push Hub user stake leaderboard HTTP ${res.status}`);
	const body = (await res.json()) as PushHubLeaderboardResponse<AddressStatRank>;
	return body.leaderboard ?? [];
}

export type CpcStakerRankResponse = {
	rows: AddressStatRank[];
	scope?: string;
	disclaimer?: string;
};

export type DataHolderRankResponse = {
	rows: AddressStatRank[];
	scope?: string;
	disclaimer?: string;
	sample_size?: number;
	updated_ms?: number;
};

/** Prefer full-chain DATA holders from Indexer scan; fall back to sampled CLOB addresses. */
export async function fetchDataHolderLeaderboard(limit = 20): Promise<DataHolderRankResponse> {
	const params = new URLSearchParams({ limit: String(limit) });
	const res = await fetch(`${getPushHubHttpUrl()}/v1/leaderboard/data_holders?${params}`);
	if (res.ok) {
		const body = (await res.json()) as PushHubLeaderboardResponse<AddressStatRank & { data_mist?: string }>;
		const rows = (body.leaderboard ?? []).map((row) => ({
			address: row.address,
			data_mist: row.data_mist,
			updated_ms: row.updated_ms,
		}));
		if (rows.length > 0 || body.scope === 'full_chain') {
			return {
				rows,
				scope: body.scope,
				disclaimer: body.disclaimer,
				sample_size: body.sample_size,
				updated_ms: body.updated_ms,
			};
		}
	}
	const sampled = await fetchDataBalanceLeaderboard(limit);
	return {
		rows: sampled,
		scope: 'sampled',
		disclaimer: 'Sampled from CLOB-known addresses via fullnode RPC',
	};
}

export async function fetchDataBalanceLeaderboard(limit = 20): Promise<AddressStatRank[]> {
	const params = new URLSearchParams({ limit: String(limit) });
	const res = await fetch(`${getPushHubHttpUrl()}/v1/leaderboard/data_balances?${params}`);
	if (!res.ok) throw new Error(`Push Hub DATA balance leaderboard HTTP ${res.status}`);
	const body = (await res.json()) as PushHubLeaderboardResponse<AddressStatRank>;
	return body.leaderboard ?? [];
}

/** Prefer full-chain stakers from Indexer scan; fall back to sampled CLOB addresses. */
export async function fetchCpcStakerLeaderboard(limit = 20): Promise<CpcStakerRankResponse> {
	const params = new URLSearchParams({ limit: String(limit) });
	const res = await fetch(`${getPushHubHttpUrl()}/v1/leaderboard/stakers?${params}`);
	if (res.ok) {
		const body = (await res.json()) as PushHubLeaderboardResponse<AddressStatRank>;
		const rows = (body.leaderboard ?? []).map((row) => ({
			address: row.address,
			stake_mist: row.stake_mist,
			updated_ms: row.updated_ms,
		}));
		if (rows.length > 0 || body.scope === 'full_chain') {
			return { rows, scope: body.scope, disclaimer: body.disclaimer };
		}
	}
	const sampled = await fetchUserStakeLeaderboard(limit);
	return {
		rows: sampled,
		scope: 'sampled',
		disclaimer: 'Sampled from CLOB-known addresses via fullnode RPC',
	};
}

export function channelsFromHealth(
	health: PushHubHealth,
	klineInterval: string,
	preferredPair?: string,
): { depthCh: string; tradeCh: string; klineCh: string; ordersCh: string } {
	const pair =
		preferredPair?.trim() ||
		(Array.isArray((health as { pairs?: string[] }).pairs)
			? (health as { pairs?: string[] }).pairs![0]
			: undefined) ||
		health.pair ||
		DEFAULT_PAIR;
	const defaults = defaultPushHubChannels(klineInterval, pair);
	const ordersCh = health.orders_ch?.trim() || defaults.ordersCh;
	if (preferredPair?.trim()) {
		return { ...defaults, ordersCh };
	}
	const prefix = (health.kline_ch_prefix ?? `kline:${pair}:`).trim();
	return {
		depthCh: health.depth_ch ?? defaults.depthCh,
		tradeCh: health.trade_ch ?? defaults.tradeCh,
		klineCh: prefix.endsWith(':') ? `${prefix}${klineInterval}` : `${prefix}:${klineInterval}`,
		ordersCh,
	};
}

export function pushTradeToClobRow(tr: PushTradeWire): ClobTradeRow | null {
	const priceTicks = String(tr.price_ticks ?? '').trim();
	const qtyLots = String(tr.qty_lots ?? '').trim();
	if (!priceTicks || !qtyLots) return null;
	const view = priceTicksToView(priceTicks);
	const rawSide = String(tr.taker_side ?? '').trim().toLowerCase();
	const tradeSide =
		rawSide === 'buy' || rawSide === 'sell' ? rawSide : ('unknown' as const);
	return {
		...view,
		priceTicks,
		qtyLots,
		timestampMs: tr.timestamp_ms ?? null,
		digest: String(tr.tx_digest ?? ''),
		maker: String(tr.maker ?? ''),
		taker: String(tr.taker ?? ''),
		tradeSide,
	};
}

export function tradeRowKey(row: ClobTradeRow): string {
	return `${row.digest}:${row.priceTicks}:${row.qtyLots}:${row.timestampMs ?? ''}`;
}

export function mergeTrades(
	existing: ClobTradeRow[],
	incoming: ClobTradeRow[],
	max = 200,
): ClobTradeRow[] {
	const seen = new Set<string>();
	const out: ClobTradeRow[] = [];
	for (const row of [...incoming, ...existing]) {
		const k = tradeRowKey(row);
		if (seen.has(k)) continue;
		seen.add(k);
		out.push(row);
	}
	out.sort((a, b) => (b.timestampMs ?? 0) - (a.timestampMs ?? 0));
	return out.slice(0, max);
}

export async function fetchPushHubTrades(limit = 100, pair?: string): Promise<ClobTradeRow[]> {
	const params = new URLSearchParams({ limit: String(limit) });
	if (pair) params.set('pair', pair);
	const res = await fetch(`${getPushHubHttpUrl()}/v1/trades?${params}`);
	if (!res.ok) throw new Error(`Push Hub trades HTTP ${res.status}`);
	const body = (await res.json()) as { trades?: PushTradeWire[] };
	return (body.trades ?? [])
		.map(pushTradeToClobRow)
		.filter((r): r is ClobTradeRow => r != null);
}

export type PushOrderWire = {
	pair_id?: number;
	order_id?: string;
	owner?: string;
	is_ask?: boolean;
	price_ticks?: string;
	original_qty_lots?: string;
	remaining_qty_lots?: string;
	status?: string;
	placed_at_ms?: number;
	closed_at_ms?: number;
	placed_tx_digest?: string;
};

export type PushHubOrderRow = {
	pairId: number;
	orderId: string;
	isAsk: boolean;
	priceTicks: string;
	/** DATA per DGC（主报价，与限价输入一致） */
	dataPerDgc: string;
	dgcPerData: string;
	originalQtyLots: string;
	remainingQtyLots: string;
	status: 'open' | 'filled' | 'cancelled' | 'partial';
	placedAtMs: number | null;
	closedAtMs: number | null;
	owner: string;
};

export function pushOrderToRow(o: PushOrderWire): PushHubOrderRow | null {
	const orderId = String(o.order_id ?? '').trim();
	const priceTicks = String(o.price_ticks ?? '').trim();
	if (!orderId || !priceTicks) return null;
	const view = priceTicksToView(priceTicks);
	const statusRaw = String(o.status ?? 'open').trim().toLowerCase();
	const status =
		statusRaw === 'filled' || statusRaw === 'cancelled' || statusRaw === 'partial'
			? statusRaw
			: statusRaw === 'open'
				? 'open'
				: 'filled';
	return {
		pairId: Number(o.pair_id ?? 1) || 1,
		orderId,
		isAsk: Boolean(o.is_ask),
		priceTicks,
		dataPerDgc: view.dataPerDgc,
		dgcPerData: view.dgcPerData,
		originalQtyLots: String(o.original_qty_lots ?? ''),
		remainingQtyLots: String(o.remaining_qty_lots ?? ''),
		status,
		placedAtMs: o.placed_at_ms ?? null,
		closedAtMs: o.closed_at_ms ?? null,
		owner: String(o.owner ?? ''),
	};
}

export async function fetchPushHubOrders(
	limit = 100,
	status: 'history' | 'open' | 'all' = 'history',
	owner?: string,
	pair?: string,
): Promise<PushHubOrderRow[]> {
	const params = new URLSearchParams({
		limit: String(limit),
		status,
	});
	if (owner?.trim()) params.set('owner', owner.trim());
	if (pair?.trim()) params.set('pair', pair.trim());
	const res = await fetch(`${getPushHubHttpUrl()}/v1/orders?${params}`);
	if (!res.ok) throw new Error(`Push Hub orders HTTP ${res.status}`);
	const body = (await res.json()) as { orders?: PushOrderWire[] };
	return (body.orders ?? [])
		.map(pushOrderToRow)
		.filter((r): r is PushHubOrderRow => r != null);
}

export function isOpenHubOrder(row: PushHubOrderRow): boolean {
	if (row.status !== 'open' && row.status !== 'partial') return false;
	try {
		return BigInt(row.remainingQtyLots || '0') > 0n;
	} catch {
		return false;
	}
}

export function upsertHubOrder(rows: PushHubOrderRow[], next: PushHubOrderRow): PushHubOrderRow[] {
	const i = rows.findIndex((r) => r.orderId === next.orderId && r.pairId === next.pairId);
	if (i < 0) return [next, ...rows];
	const out = [...rows];
	out[i] = next;
	return out;
}

/** Apply one live order push: open/partial stays in open, filled/cancelled moves to history. */
export function applyPushedOrder(
	open: PushHubOrderRow[],
	history: PushHubOrderRow[],
	row: PushHubOrderRow,
): { open: PushHubOrderRow[]; history: PushHubOrderRow[] } {
	const drop = (list: PushHubOrderRow[]) =>
		list.filter((r) => !(r.orderId === row.orderId && r.pairId === row.pairId));
	if (isOpenHubOrder(row)) {
		return { open: upsertHubOrder(drop(open), row), history: drop(history) };
	}
	return { open: drop(open), history: upsertHubOrder(drop(history), row) };
}

export function filterHubOrdersForPair(rows: PushHubOrderRow[], pairId?: number): PushHubOrderRow[] {
	if (!pairId || pairId <= 0) return rows;
	return rows.filter((r) => r.pairId === pairId);
}

export type PushHubBridgeEvent = {
	id: string;
	direction: 'usdt-to-data' | 'data-to-usdt' | string;
	nonce: string;
	cpc_address: string;
	counterparty: string;
	amount_mist: string;
	amount_wei: string;
	dgc_digest: string;
	bsc_tx: string;
	status: string;
	timestamp_ms: number;
	bsc_block?: number;
};

export async function fetchPushHubBridgeHistory(
	address: string,
	limit = 50,
): Promise<PushHubBridgeEvent[]> {
	const hex = tryNormalizeToHexAddress(address) || address.trim();
	const params = new URLSearchParams({
		address: hex,
		limit: String(limit),
	});
	const res = await fetch(`${getPushHubHttpUrl()}/v1/bridge/history?${params}`);
	if (!res.ok) throw new Error(`Push Hub bridge history HTTP ${res.status}`);
	const body = (await res.json()) as { events?: PushHubBridgeEvent[] };
	return body.events ?? [];
}
